import { toaster } from '@optiaxiom/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getSpecializedAgentKey } from '../services/specialized-agent';
import {
  approveAgentShareRequest,
  getAgentShareRequest,
  rejectAgentShareRequest,
} from '../services/unifiedAgents';

export function useAgentShareRequest(
  instanceId: string,
  agentType: 'specialized' | 'workflow',
  agentId: string,
  enabled = true
) {
  const { key, queryFn } = getAgentShareRequest(instanceId, agentType, agentId);
  const { data } = useQuery({
    queryKey: key,
    queryFn,
    enabled: enabled && !!instanceId && !!agentId,
  });

  return { pendingShareRequest: data?.pending_share_request ?? null };
}

export function useResolveAgentShareRequest(
  instanceId: string,
  agentType: 'specialized' | 'workflow',
  agentId: string
) {
  const queryClient = useQueryClient();
  const shareRequestKey = getAgentShareRequest(instanceId, agentType, agentId).key;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: shareRequestKey });
    void queryClient.invalidateQueries({ queryKey: getSpecializedAgentKey(agentId) });
    void queryClient.invalidateQueries({ queryKey: ['agents'] });
  };

  const approve = useMutation({
    mutationFn: (requestId: string) =>
      approveAgentShareRequest(instanceId, agentType, agentId, requestId),
    onSuccess: () => {
      invalidate();
      toaster.create('Agent shared with your organization', { type: 'success' });
    },
    onError: () => {
      toaster.create('Failed to approve sharing request', { type: 'danger' });
    },
  });

  const reject = useMutation({
    mutationFn: (requestId: string) =>
      rejectAgentShareRequest(instanceId, agentType, agentId, requestId),
    onSuccess: () => {
      invalidate();
      toaster.create('Sharing request declined', { type: 'success' });
    },
    onError: () => {
      toaster.create('Failed to decline sharing request', { type: 'danger' });
    },
  });

  return { approve, reject };
}
